import React, { useCallback, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import Button from '@mui/material/Button';
import {
  Avatar,
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { CloseRounded, SearchRounded } from '@mui/icons-material';
import { toast } from 'react-toastify';

import { stringAvatar } from '../../../../utils/avatarGenerator';

import { Guest } from '../../../../services';
import {
  createFolderRequest,
  updateFolderRequest,
} from '../../../../services/requests/pastas/utils';
import { Folder } from '../../../../services/requests/pastas/types';

import { Input } from '../../../../components/Input';

import {
  AmountSelectedUsers,
  FolderType,
  FolderTypeTitle,
  HeaderDrawer,
  SelectedAllBtn,
  Text,
  TextBtn,
  Users,
} from './styles';

import useAuthStore from '../../../../store/auth';
import useCompanyStore from '../../../../store/companies';

interface GuestToSelect extends Guest {
  selectedFolder: boolean;
}

interface DrawerCreateFile {
  setOpenDrawerCreateFolder: (type: boolean) => void;
  openDrawerCreateFolder: boolean;
  listagemPastas: Folder[] | undefined;
  setListagemPastas(b: Folder[]): void;
  listagemUsers: GuestToSelect[];
  folderId: string;
  onLoad: any;
  folderName: string | undefined;
  isNew?: boolean;
  folderSelected?: any;
  setFolderSelected?: any;
  searchForUsers: string;
  setSearchForUsers: (t: string) => void;
  loading: boolean;
  setLoadingFolder: (type: boolean) => void;
}

export default function DrawerCreateFile({
  setOpenDrawerCreateFolder,
  openDrawerCreateFolder,
  listagemUsers,
  setListagemPastas,
  listagemPastas,
  folderId,
  onLoad,
  folderName,
  isNew = false,
  folderSelected,
  setFolderSelected,
  searchForUsers,
  setSearchForUsers,
  loading,
  setLoadingFolder,
}: DrawerCreateFile) {
  const user = useAuthStore(state => state.user);
  const selectedCompany = useCompanyStore(state => state.selectedCompany);

  const [listaConvidadosPastas, setListaConvidadosPastas] = useState<Guest[]>(
    [],
  );
  const [listFoldersUsers, setListFoldersUsers] = useState<GuestToSelect[]>([]);
  const [listUsersFiltered, setListUsersFiltered] = useState<GuestToSelect[]>(
    [],
  );
  const [folderType, setFolderType] = useState('public');
  const [selectedAll, setSelectedAll] = useState<boolean | null>(null);

  const formValidation = useCallback((): any => {
    return Yup.object().shape({
      name: Yup.string()
        .required('O nome é obrigatório.')
        .min(3, 'O nome precisa ter no mínimo 3 caracteres'),
    });
  }, []);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isSubmitting },
  } = useForm({
    resolver: yupResolver(formValidation()),
  });

  useEffect(() => {
    if (listagemUsers?.length > 0) {
      const guests = listagemUsers.map((u: Guest) => ({
        ...u,
        profile: 'GS2',
        selectedFolder: false,
      }));

      const guestsWithoutOwner = guests.filter(u => u?.id !== user?.id);

      setListFoldersUsers(guestsWithoutOwner as any);
    }
  }, [listagemUsers]);

  useEffect(() => {
    const filterUser = listFoldersUsers?.filter(users =>
      users.name.toUpperCase().includes(searchForUsers.toUpperCase()),
    );
    setListUsersFiltered(
      filterUser?.length > 0 ? filterUser : listUsersFiltered,
    );
  }, [searchForUsers, openDrawerCreateFolder]);

  useEffect(() => {
    if (folderId && !isNew) {
      setValue('name', folderName);
    }
  }, [setValue, folderId, folderName, isNew]);

  useEffect(() => {
    if (selectedAll === null) {
      const usersSelected = listUsersFiltered?.map(u => ({
        ...u,
      }));
      setListUsersFiltered([...usersSelected]);
      setListFoldersUsers([...usersSelected]);
      setListaConvidadosPastas([...usersSelected]);
    } else if (selectedAll === false) {
      const usersUnselected = listUsersFiltered?.map(u => ({
        ...u,
        selectedFolder: false,
      }));
      setListUsersFiltered([...usersUnselected]);
      setListFoldersUsers([...usersUnselected]);
      setListaConvidadosPastas([]);
    } else if (selectedAll === true) {
      const usersSelected = listUsersFiltered?.map(u => ({
        ...u,
        selectedFolder: true,
      }));
      setListUsersFiltered([...usersSelected]);
      setListFoldersUsers([...usersSelected]);
      setListaConvidadosPastas([...usersSelected]);
    }
  }, [selectedAll]);

  useEffect(() => {
    if (openDrawerCreateFolder) {
      setSelectedAll(null);
    }
  }, [openDrawerCreateFolder]);

  const handleUserToInviteFolder = useCallback(
    (
      event: React.MouseEvent<unknown>,
      name: string,
      usr: GuestToSelect,
      idx: number,
      type: 'select',
    ) => {
      let listaUsuariosPastasEditada = { ...usr };

      if (type === 'select') {
        listaUsuariosPastasEditada.selectedFolder = !usr?.selectedFolder;
      }

      if (searchForUsers?.length > 0) {
        const listFilteredWhenSearching = listFoldersUsers?.map(u => {
          if (u?.id === usr?.id) {
            setSearchForUsers('');
            return listaUsuariosPastasEditada;
          }
          return u;
        });
        const usuariosMembros = listFilteredWhenSearching?.filter(
          u => u.selectedFolder,
        );
        setListaConvidadosPastas([...usuariosMembros!]);
        setListFoldersUsers(listFilteredWhenSearching);
        setListUsersFiltered(listFilteredWhenSearching);
      } else {
        listFoldersUsers?.splice(idx, 1, listaUsuariosPastasEditada);
        listUsersFiltered?.splice(idx, 1, listaUsuariosPastasEditada);
        setListUsersFiltered([...listUsersFiltered!]);
        setListFoldersUsers([...listFoldersUsers!]);
        const usuariosMembros = listFoldersUsers?.filter(u => u.selectedFolder);
        setListaConvidadosPastas([...usuariosMembros!]);
      }
    },
    [
      listFoldersUsers,
      listUsersFiltered,
      searchForUsers?.length,
      setSearchForUsers,
    ],
  );

  const handleFolder = useCallback(
    async (formData: any) => {
      try {
        setLoadingFolder(true);
        const guests = listaConvidadosPastas?.map(m => ({
          id: m.id,
          name: m.name,
          email: m.email,
          profile: m.profile,
          status: m.status,
          avatar_url: m.avatar_url,
        }));

        const guestsWithOwner = guests?.concat({
          id: user?.id as string,
          name: user?.name as string,
          email: user?.email as string,
          profile: 'GS1',
          status: user?.status as 'US1' | 'US2' | 'US3',
          avatar_url: user?.avatar_url,
        });

        const guests_ids = guestsWithOwner?.map(m => m.id);

        const data = {
          name: formData.name,
          is_automatic: false,
          is_main: !isNew,
          childrens: [],
          childrens_ids: [],
          guests: folderType === 'public' ? [] : guestsWithOwner,
          guests_ids: folderType === 'public' ? [] : guests_ids,
        };

        let response: any;

        if (folderId && !isNew) {
          response = await updateFolderRequest(
            data as any,
            folderId,
            user?.id as string,
            selectedCompany?.id as string,
          );

          toast.success('Pasta editada com sucesso');
        } else {
          let folderIdExists;

          if (isNew && folderId) {
            folderIdExists = folderSelected?.id;
          } else {
            folderIdExists = 'null';
          }
          response = await createFolderRequest(
            data as any,
            user?.id as string,
            selectedCompany?.id as string,
            folderIdExists,
          );

          toast.success('Pasta criada com sucesso');
        }

        if (response?.status === 201 || response?.status === 200) {
          const novaPasta = {
            ...response?.data,
            type: 'folder',
          };

          reset();
          setListagemPastas([...listagemPastas!, novaPasta]);
          setSelectedAll(null);
          onLoad();

          if (isNew && folderSelected) {
            const updatedFolder = {
              childrens: [...folderSelected.childrens, { ...novaPasta }],
              childrens_ids: [...folderSelected.childrens_ids, novaPasta.id],
            };

            //   const respFolder = await updateFolderRequest(
            //     updatedFolder as any,
            //     folderId,
            //     user?.id as string,
            //     selectedCompany?.id as string,
            //   );

            // if (respFolder?.status === 200) {
            setFolderSelected({ ...folderSelected, ...updatedFolder });
            // }
          }
        }
        setOpenDrawerCreateFolder(false);
      } catch (err) {
        console.log(err);
      } finally {
        setLoadingFolder(false);
      }
    },
    [
      setLoadingFolder,
      listaConvidadosPastas,
      user?.id,
      user?.name,
      user?.email,
      user?.status,
      user?.avatar_url,
      isNew,
      folderType,
      folderId,
      setOpenDrawerCreateFolder,
      selectedCompany?.id,
      folderSelected,
      reset,
      setListagemPastas,
      listagemPastas,
      onLoad,
      setFolderSelected,
    ],
  );

  const handleSelectedAll = () => {
    setSelectedAll(prev => !prev);
  };

  const getLabelForAmount = () => {
    const usersSelecteds = listFoldersUsers?.filter(u => u?.selectedFolder);

    if (usersSelecteds?.length === 1) {
      return `${usersSelecteds?.length} selecionado`;
    }
    return `${usersSelecteds?.length} selecionados`;
  };

  return (
    <Box
      width="480px"
      padding="20px"
      height="100%"
      display="flex"
      flexDirection="column"
    >
      <HeaderDrawer>
        <CloseRounded
          sx={{ cursor: 'pointer', position: 'absolute', left: '0' }}
          onClick={() => setOpenDrawerCreateFolder(false)}
        />
        <span className="title">
          {folderId ? 'Criar pasta' : 'Criar pasta'}
        </span>
      </HeaderDrawer>
      <Box height="100%">
        <form onSubmit={handleSubmit(handleFolder)} style={{ height: '100%' }}>
          <Box height="100%" display="flex" flexDirection="column">
            <Input
              label="Nome da pasta"
              name="name"
              control={control}
              placeholder="Nome da pasta"
              sx={{ marginTop: '30px' }}
              fullWidth
            />
            <FolderType>
              <FolderTypeTitle>Tipo da pasta</FolderTypeTitle>
              <RadioGroup
                row
                aria-labelledby="demo-row-radio-buttons-group-label"
                name="row-radio-buttons-group"
                value={folderType}
              >
                <FormControlLabel
                  value="public"
                  control={<Radio />}
                  label="Pública"
                  onChange={() => setFolderType('public')}
                />
                <FormControlLabel
                  value="private"
                  control={<Radio />}
                  label="Compartilhar com"
                  onChange={() => setFolderType('private')}
                />
              </RadioGroup>
            </FolderType>
            {folderType === 'private' &&
              (listUsersFiltered?.length > 0 ? (
                <>
                  <TextField
                    variant="filled"
                    placeholder="Filtrar usuário..."
                    fullWidth
                    value={searchForUsers}
                    onChange={ev => setSearchForUsers(ev.target.value)}
                    InputProps={{
                      endAdornment: <SearchRounded />,
                    }}
                    sx={{ marginTop: '10px' }}
                  />
                  {loading ? (
                    <Box
                      width="100%"
                      display="flex"
                      justifyContent="center"
                      mt="20px"
                    >
                      <CircularProgress />
                    </Box>
                  ) : (
                    <>
                      <Box flex="1 0 0" overflow="auto" marginBottom="20px">
                        {searchForUsers?.length === 0 && (
                          <SelectedAllBtn onClick={handleSelectedAll}>
                            <Checkbox
                              color="primary"
                              checked={selectedAll || false}
                              sx={{
                                padding: '0',
                              }}
                            />
                            <TextBtn>Selecionar todos</TextBtn>
                          </SelectedAllBtn>
                        )}
                        {listUsersFiltered?.map((u: any, index: number) => (
                          <Users
                            onClick={event =>
                              handleUserToInviteFolder(
                                event,
                                u.name,
                                u,
                                index,
                                'select',
                              )
                            }
                            key={u?.id}
                          >
                            <Checkbox
                              color="primary"
                              checked={u.selectedFolder === true}
                              sx={{ padding: '0' }}
                            />
                            <Box display="flex" flexDirection="row" gap="10px">
                              {u?.avatar_url ? (
                                <Avatar sx={{ width: '40px' }}>
                                  <img
                                    src={u?.avatar_url as string}
                                    alt="Foto de perfil"
                                    style={{ width: '100%' }}
                                  />
                                </Avatar>
                              ) : (
                                <Avatar
                                  {...stringAvatar(u.name!)}
                                  sx={{ width: '40px' }}
                                />
                              )}
                            </Box>
                            <span className="name">{u.name}</span>
                          </Users>
                        ))}
                      </Box>
                    </>
                  )}
                </>
              ) : (
                <Box marginTop="10px" flex="1 0 0">
                  Não há nenhum usuário convidado para adicionar
                </Box>
              ))}
            <Box
              width="100%"
              display="flex"
              justifyContent="end"
              flex={folderType === 'private' ? '0 0 0' : '1 0 0'}
              alignItems={folderType === 'private' ? 'center' : 'flex-end'}
              gap="10px"
            >
              {folderType === 'private' && (
                <AmountSelectedUsers>{getLabelForAmount()}</AmountSelectedUsers>
              )}
              <Button
                type="submit"
                variant="contained"
                color="success"
                disabled={isSubmitting}
                sx={{
                  color: '#fff',
                  fontWeight: '500',
                  fontSize: '15px',
                }}
              >
                Salvar
              </Button>
            </Box>
          </Box>
        </form>
      </Box>
    </Box>
  );
}
